<template>
  <div class="booking-component text-none pa-6">
    <div v-if="reservations.length" class="main-header text-left mb-16 mb-md-6 mt-0">
      {{$t('reservation.list.main_header')}}
    </div>

    <div class="info-wrapper">
      <div v-if="!isLoading && !reservations.length" :class="['info d-flex flex-column justify-center mt-4 mb-2 mx-4 py-12 px-9', $vuetify.display.mobile ? 'mobile' : 'text-left']">
        <div class="header mb-6 mb-md-4">
          {{$t('reservation.list.empty_header')}}
        </div>
        <div class="subheader">
          {{$t('reservation.list.empty_subheader')}}
        </div>
      </div>
      <v-progress-circular
        v-if="isLoading"
        indeterminate
        color="primary"
        :size="50"
        :width="5"
      ></v-progress-circular>
    </div>
    <div v-if="!isLoading && reservations.length" :key="refresh_key">
      <div v-for="(element, index) in reservations" :key="index" class="mt-2">
        <reservation-component 
          v-if="element.type=='reservation'" 
          :reservation="element" 
          @refresh="init" 
          @compare-dialog="hasOpenCompareDialog"/>
        <transport-component v-if="element.type=='transport'" :transport="element" @refresh="init"/>
      </div>
      <v-navigation-drawer v-if="paymentReservations.length"
        :class="['payment-reservation-list-drawer invicta pa-6', $vuetify.display.mobile ? 'mobile' : '']"
        :absolute="$vuetify.display.mobile ? true : false"
        :mobile="$vuetify.display.mobile ? true : false"
        :location="$vuetify.display.mobile ? 'bottom' : 'left'"
        :border="0"
        :width="402">

        <div class="main-header text-left ml-2 mt-2 mb-4">
          {{$t('reservation.list.payments_header')}}
        </div>

        <v-divider class="py-2"></v-divider>

        <div class="mb-6" v-for="(element, index) in paymentReservations" :key="index">

          <div class="text-left d-flex justify-space-between mt-3 value">
            <span class="mr-2">{{$t('reservation.number_label')}} #{{element.reservation_id}}</span>
            <span>{{$n(element.reservation_data.price * element.reservation_data.quantity, 'currency', 'pl')}}</span>
          </div>

          <div class="text-left mb-4 subvalue">
            {{$t('reservation.product_quantity_label')}} {{element.reservation_data.quantity}}
          </div>

          <div
            v-if="element.reservation_data.mz_grant_file_id && element.reservation_data.mz_grant"
            class="d-flex justify-space-between value"
          >
            <span class="discount-label text-left">
              {{ element.reservation_data.material_type == 'mtCell' ? $t('reservation.discount_value_cell') : $t('reservation.discount_value_semen')}}
            </span>
            <span class="discount-price">
              - {{$n(element.reservation_data.price * element.reservation_data.quantity, 'currency', 'pl')}}
            </span>
          </div>

        </div>

        <v-divider class="py-2"></v-divider>

        <div v-if="paymentLocationOfTreatment && paymentLocationOfTreatment?.transport.length">
          <div v-for="(t, index) in paymentLocationOfTreatment.transport" :key="index">
            <div class="d-flex justify-space-between mt-3 value">
              <span class="text-left">
                {{ $t("reservation.transport") }}
              </span>
              <span>{{ $n(parseInt(t.price), 'currency', 'pl') }}</span>
            </div>
            <div class="text-left mb-6 subvalue">
              {{ $t(t.location_code) }}
              <img :src="shevronIcon" height="8" class="mx-2"/>
              {{paymentLocationOfTreatment.name}}
            </div>
          </div>
          <v-divider class="py-2"></v-divider>
        </div>

        <div class="main-header text-left mt-4 mb-6">{{$t('user.personal_data.location_choice_header')}}</div>

        <div class="input-wrapper mb-6">
          <div class="value text-left mb-2">{{$t('user.personal_data.location_label')}}</div>
          <div class="select-wrapper mb-4">
            <Select
              v-if="transportPrices && transportPrices.length"
              v-model="paymentLocationOfTreatment"
              id="location"
              :clearIcon="false"
              :options="transportPrices"
              :placeholder="$t('select_from_list_label')"
              :sortData="false"
              @change="changeLocation">
              <template #input="{ modelValue }">
                <div class="text-left d-flex flex-row justify-space-between w-100 select-location" style="white-space: break-spaces">
                  <div>{{ modelValue?.name ? $t('INVICTA-CLINIC') + ' ' + modelValue.name : $t('select_from_list_label') }}</div>
                  <div v-if="modelValue && modelValue?.name && modelValue.transport.length" class="transport-price mr-6">
                    + {{$n(modelValue.transport.reduce((accumulator, currentValue) => accumulator + parseInt(currentValue.price), 0) | 0, 'currency', 'pl')}}
                  </div>
                </div>
              </template>
              <template #option-top>
                <div class="option-top-label pa-4 pa-md-3 d-flex flex-row justify-space-between w-100">
                  <span>{{$t('user.personal_data.location_label')}}</span>
                  <span>{{$t('transport.price_label')}}</span>
                </div>
              </template>
              <template #option="{ option }">
                <div class="text-left d-flex flex-column w-100">
                  <div
                    class="text-left d-flex flex-row justify-space-between w-100"
                    style="white-space: break-spaces"
                    :class="{'option-disabled': option.id == -1}"
                  >
                    <div>{{$t('INVICTA-CLINIC')}} {{ option.name }}</div>
                    <div class="transport-price" v-if="option.transport.length">
                      + {{$n(option.transport.reduce((accumulator, currentValue) => accumulator + parseInt(currentValue.price), 0) | 0, 'currency', 'pl')}}
                    </div>
                  </div>
                  <div class="transport-time-info d-flex flex-wrap">
                    {{ $t('transport.estimated_time_label') }}: 
                    <span class="mx-1">
                      {{ option.transport.length ? 
                        $t('transport.n_weeks_waiting_info') : 
                        $t('transport.material_at_clinic_info') }}
                    </span>
                  </div>
                </div>
              </template>
            </Select>
          </div>
        </div>

        <div class="consent-wrapper text-left mt-4 mb-8">
          <div class="consent" v-if="isTransportNeeded">
            <label class="container input-label mt-2 d-flex flex-row align-start">
              <span> {{$t('reservation.transport_consent_content')}}
                <a class='text-decoration-underline' @click="showTransportConditionsDialog">
                  {{$t('reservation.transport_conditions_text_link')}}
                </a>.*
              </span>
              <input type="checkbox"
                v-model="transportConsent">
              <span class="checkmark"></span>
            </label>
          </div>
        </div>

        <v-divider class="py-2"></v-divider>

        <div class="text-left d-flex justify-space-between mt-6 value">
          <span>{{$t('reservation.total_order_value_label')}}</span>
          <span>{{$n(reservationsPrice + transportPrice, 'currency', 'pl')}}</span>
        </div>
        <div class="text-left d-flex justify-space-between mt-1 mb-8 whole_value">
          <span>{{$t('reservation.paid_order_value_label')}}</span>
          <span>{{$n(paidReservationsPrice + transportPrice, 'currency', 'pl')}}</span>
        </div>

        <v-btn
          color="primary"
          variant="flat"
          height="40"
          rounded
          block
          class="mb-md-0 mb-4 mt-4 py-2 px-6"
          @click="payForCheckedReservations()"
          :disabled="fetchingMultiPaymentLink || !paymentLocationOfTreatment || (isTransportNeeded && !transportConsent)"
        >
          {{(paidReservationsPrice + transportPrice) ? $t('reservation.list.pay_btn') : $t('reservation.reserve_btn')}}
          <v-icon
            v-if="fetchingMultiPaymentLink"
            size="16"
            icon="mdi-loading mdi-spin"
            color="white"
            class="ml-2"
          ></v-icon>
        </v-btn>

        <v-btn
          variant="flat"
          height="40"
          rounded
          block
          class="clear_payment_reservation_list mb-md-0 mb-4 mt-4 py-2 px-6"
          @click="$store.dispatch('reservationModule/clearPaymentReservations')"
        >
          {{$t('reservation.list.clear_payments_btn')}}
        </v-btn>

      </v-navigation-drawer>
      <div v-if="paymentReservations.length && $vuetify.display.mobile"
        class="payment_reservation_nav_wrapper d-flex justify-center"
      >
        <div class="payment_reservation_nav mx-4 mt-3 mb-2">
          <div class="text-left d-flex justify-space-between mb-2 whole_value">
            <span>{{$t('reservation.total_order_value_label')}} ({{paymentReservations.length}})</span>
            <span>{{$n(paidReservationsPrice + transportPrice, 'currency', 'pl')}}</span>
          </div>
          <v-btn
            color="primary"
            variant="flat"
            height="40"
            rounded
            block
            @click="goToPayment()"
          >
            {{$t('reservation.list.go_to_payment_btn')}}
          </v-btn>
        </div>
      </div>
      <v-dialog 
        max-width="800"
        v-model="transportConditionsDialogVisible"
        :fullscreen="$vuetify.display.mobile"
        :scrollable="true"
        no-click-animation
        >
        <v-card>
          <v-btn flat icon color="transparent" size="x-small" class="close" @click="transportConditionsDialogVisible = false">
            <v-icon color="primary" size="24" icon="mdi-close"></v-icon>
          </v-btn>
          <v-spacer v-if="$vuetify.display.mobile"></v-spacer>
          <transport-conditions-component/>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { useStore } from 'vuex';
import ReservationComponent from './ReservationComponent.vue';
import TransportComponent from './TransportComponent.vue';
import TransportConditionsComponent from '@/components/TransportConditionsComponent.vue'
import reservationUtil from "@/utils/reservation_util";
import { Select } from "design-system-ui-components";
import axios from "axios";

export default {
    name: "booking-component",
    data: () => {
      return {
        reservations: [],
        fetchingMultiPaymentLink: false,
        isLoading: false,
        prices: null,
        refresher: null,
        refresh_key: 0,
        transportPrices: [],
        paymentLocationOfTreatment: null,
        transportConsent: false,
        transportConditionsDialogVisible: false,
        openedCompareDialog: false
      }
    },
    components: {
      ReservationComponent,
      TransportComponent,
      TransportConditionsComponent,
      Select
    },
    setup() {
      const store = useStore()
      const shevronIcon = computed({
          get: () => { return require('../assets/icons/chevron-right.svg'); }
      })

      return {
        shevronIcon,
        paymentReservations: computed(() => store.getters['reservationModule/getPaymentReservations'])
      }
    },
    watch: {
      'paymentReservations': {
        handler() {
          this.fetchTransportPrices()
        }
      },
    },
    created() {
      this.$watch('$i18n.locale', () => {
        setTimeout(() => {
          if (!this.paymentReservations) {
            this.init()
          }
        }, 1000)
      })
    },
    mounted() {
      this.isLoading = true;
      this.init();
      this.fetchTransportPrices();

      this.refresher = setInterval(() => {
        if (!this.openedCompareDialog) {
          this.init();
          this.refresh_key++;
        }
      }, 60 * 1000);
      this.$store.dispatch('breadcrumbModule/setRoute', [{
          name: 'breadcrumb.want_to_be_a_parent',
          url: `https://bank.invicta.pl${ this.$t('bank_invicta_path.planning_parenthood') }`,
          absolute: true
        },{
          name: 'breadcrumb.dashboard',
          url: '/dashboard?selected=personal-details'
        },
        {
          name: 'breadcrumb.dashboard_labels.reservations',
          url: ''
        }
      ])
    },
    unmounted() {
      clearInterval(this.refresher);
    },
    computed: {
      reservationsPrice() {
        return this.paymentReservations
          .reduce((a,e) => a += parseInt(e.reservation_data?.price * e.reservation_data?.quantity), 0) | 0;
      },
      paidReservationsPrice() {
        return this.paymentReservations.reduce((a,e) => a += parseInt(e.reservation_data?.total_price), 0) | 0;
      },
      transportPrice() {
        if (this.paymentLocationOfTreatment) {
          return this.paymentLocationOfTreatment?.transport.reduce((a,e) => a += parseInt(e.price), 0) | 0;
        } else {
          return 0;
        }
      },
      isTransportNeeded() {
        return this.paymentLocationOfTreatment?.transport?.length ? true : false
      }
    },
    methods: {
      hasOpenCompareDialog(d) {
        this.openedCompareDialog = d
      },
      init() {
        axios.get('/api/reservation')
        .then((response) => {
          this.reservations_list = response.data.reservations.map(r => { return {...r, type: "reservation"}});
          this.transports_list = response.data.transports.map(t => { return {...t, type: "transport"}});

          this.reservations = [];

          this.reservations_list.forEach(r => {
            let transport = this.transports_list.find(e => e.reservation_ids.includes(r.reservation_id))

            this.reservations.push(r)
            let loadedTransportReservationsCount = 0
            if (transport) {
              transport.reservation_ids.forEach(id => {
                if (this.reservations.find(e => e.reservation_id == id)) loadedTransportReservationsCount++
              })
              if (transport.reservation_ids.length == loadedTransportReservationsCount) {
                this.reservations.push(transport)
              }
            }
          })
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.isLoading = false;
        })
      },
      payForCheckedReservations() {
        if (this.paymentLocationOfTreatment) {
          this.fetchingMultiPaymentLink=true

          axios.put(`/api/reservation/payment`, {
            selected_reservations: this.paymentReservations.map(r => r.reservation_id),
            location_of_treatment_id: this.paymentLocationOfTreatment.id,
            transport_consent: this.transportConsent,
            return_url: this.getPaymentReturnUrlWithParameters()
          })
          .then(response => {
            if (response.data.payment_url && response.data.already_paid != true) {
              window.location.href = response.data.payment_url
              this.$store.dispatch('reservationModule/clearPaymentReservations')
            } else {
              this.fetchingMultiPaymentLink=false
            }
          })
          .catch(error => {
            this.fetchingMultiPaymentLink=false
            if (error.response) {
              console.error(error.response.data)
              if (error.response.data.error_key == 'PAYMENT_FAILURE_DUE_DIFFERENT_FILES') {
                this.$root.$alertMessageRef.showAlert(
                  this.$t(`alert.PAYMENT_FAILURE_DUE_DIFFERENT_FILES.title`),
                  this.$t(`alert.PAYMENT_FAILURE_DUE_DIFFERENT_FILES.message`),
                  {
                    link: false,
                    action: false,
                    timeout: true,
                    timeoutDuration: 5000,
                    color: 'rgba(0, 32, 91, 1)',
                    backgroundColor: 'rgba(255, 235, 235, 1)',
                    borderColor: 'rgba(214, 0, 28, 1)',
                    icon: 'mdi-close-circle-outline',
                    iconColor: 'rgba(214, 0, 28, 1)',
                    width: '600px'
                  }
                )
              }
            } else {
              console.error("PAYMENT ERROR:",error)
            }
          })
        }
      },
      getPaymentReturnUrlWithParameters() {
        return reservationUtil.getPaymentReturnUrlWithParameters(this.paymentReservations, this.$i18n.locale)
      },
      getPaymentReservationsProductKeys() {
        return this.paymentReservations.map(r => r.reservation_data?.product_key)
      },
      getPaymentReservationsUniqueTransportConsent() {
        return [...new Set(this.paymentReservations.map(r => r.reservation_data?.transport_consent))]
      },
      getPaymentReservationsLocationOfTreatmentUniqueIds() {
        return [...new Set(this.paymentReservations.map(r => r.reservation_data?.location_of_treatment_id))]
      },
      changeLocation() {
        this.transportConsent = false
      },
      showTransportConditionsDialog(e) {
        this.transportConditionsDialogVisible = true;
        e.preventDefault();
      },
      fetchTransportPrices() {
        this.transportConsent = false
        this.paymentLocationOfTreatment = null
        if (this.paymentReservations.length) {
          reservationUtil.getTransportPrices(
            this.$i18n.locale,
            this.getPaymentReservationsProductKeys()
          ).then((response) => {
            this.transportPrices = response;
            if (this.getPaymentReservationsLocationOfTreatmentUniqueIds().length == 1) {
              this.paymentLocationOfTreatment = response.find((l) => l.id == this.getPaymentReservationsLocationOfTreatmentUniqueIds()[0])

              if (this.getPaymentReservationsUniqueTransportConsent().length == 1) {
                this.transportConsent = this.getPaymentReservationsUniqueTransportConsent()[0]
              }
            }
          });
        }
      },
      goToPayment() {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth'})
      }
    }
}
</script>

<style lang="scss" scoped>

:deep(.v-navigation-drawer) {
  box-shadow: 2px 0px 16px 0px rgba(112, 144, 176, 0.20);
  &.mobile {
    box-shadow: none;
    height: auto !important;
  }
}

:deep(.selectfield--item) {
  width: 352px;
  .transport-price {
    font-size: 16px !important;
  }
}
:deep(svg.icon-expand-more) {
  color: $ds-primary !important;
}
:deep(.selectfield--selection>svg.icon-close) {
  color: $ds-primary !important;
}
.option-top-label {
  color: $ds-primary-100;
  font-size: 10px;
}
.transport-time-info {
  color: $ds-primary-100;
  font-size: 12px;
  width: 100%;
  span {
    color: $ds-primary;
  }
}
.transport-price {
  color: $ds-icon-success;
  font-size: 16px;
}

.booking-component {
  box-sizing: border-box;
  background: white;
  border: 1px solid $nsb-color-3;
}
.main-header {
  @include nsb-gentium-bold;
  font-size: 23px;
  line-height: 27px;
}

.info-wrapper {
  position: relative;
  .v-progress-circular {
    position: fixed;
    top: 50%;
    left: 50%;
    z-index: 999;
    transform: translate(-50%,-50%);
  }
  .info{
    height: 350px;
    .header {
      @include nsb-font-header-bold;
      font-size: 20px !important;
      line-height: 30px !important;
    }
    .subheader {
      @include nsb-font-header;
      font-size: 14px !important;
      line-height: 22px !important;
    }
    &.mobile {
      .header {
        font-size: 14px !important;
        line-height: 22px !important;
      }
      .subheader {
        font-size: 12px !important;
        line-height: 18px !important;
      }
    }
  }
}

.v-divider {
  border-color: var(--borders-dividers-default, #DADEE6) !important;
}

.payment-reservation-list-drawer {

  .whole_value {
      @include nsb-font-header-bold;
      letter-spacing: 0.4px;
      font-size: 14px;
      line-height: 21px;
      color: $ds-primary;
  }
  .value {
      @include nsb-font-container;
      letter-spacing: 0.4px;
      font-size: 14px;
      line-height: 22px;
      color: $ds-primary;
  }
  .subvalue {
      @include nsb-font-label;
      font-size: 12px;
      line-height: 20px;
      letter-spacing: 0.4px;
      color: $ds-primary-100;
  }

  .discount-label {
    max-width: 190px;
  }

  .discount-price {
    color: $ds-icon-success;
    font-size: 14px;
  }

  .input-wrapper {
    position: relative;
    width: 100%;
    .select-wrapper {
      position: relative;
      .select-location {
        @include nsb-font-input;
        font-size: 14px !important;
        min-height: 40px;
        width: 100%;
        outline: none;
        border: 1px solid rgb(230, 230, 231);
        padding: 8px 16px;
        border-radius: 4px;
        option:disabled {
          color: $nsb-disabled-text;
        }
        .transport-price {
          font-size: 14px;
        }
      }
      .chevron {
        position: absolute;
        right: 16px;
        top: 8px;
        pointer-events: none;
      }
    }
  }

  .consent-wrapper {
    .consent {
      .container {
        @include nsb-font-container;
        font-size: 14px !important;
        line-height: normal;
      }
    }
  }

  .clear_payment_reservation_list {
    color: $ds-primary;
  }
}

.payment_reservation_nav_wrapper {
  animation-name: example;
  animation-duration: 0.5s;
  animation-fill-mode: forwards;
  animation-timing-function: ease-in;
  box-shadow: 2px 0px 16px 0px rgba(112, 144, 176, 0.20);

  color: $ds-primary;
  position: fixed;
  bottom: 0px;
  left: 0px;
  width: 100%;
  height: auto;
  background: $ds-white !important;
  z-index: 100;
  .payment_reservation_nav {
    width: 100%;
    .whole_value {
      @include nsb-font-header-bold;
      font-size: 14px !important;
      line-height: 22px;
    }
  }
}

/* The container */
.container {
  display: block;
  position: relative;
  padding-left: 24px;
  cursor: pointer;
  user-select: none;
}

/* Hide the browser's default checkbox */
.container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

/* Create a custom checkbox */
.checkmark {
  box-sizing: border-box;
  margin-top: 2px;
  border-radius: 2px;
  border: 1px solid $ds-primary;
  position: absolute;
  top: 0;
  left: 0;
  height: 16px;
  width: 16px;
  background-color: white;
}

/* On mouse-over, add a grey background color */
.container:hover input ~ .checkmark {
  background-color: $nsb-disabled;
}

/* When the checkbox is checked, add a blue background */
.container input:checked ~ .checkmark {
  background-color: $ds-primary;
}

/* Create the checkmark/indicator (hidden when not checked) */
.checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

/* Show the checkmark when checked */
.container input:checked ~ .checkmark:after {
  display: block;
}

/* Style the checkmark/indicator */
.container .checkmark:after {
  left: 5px;
  top: 2px;
  width: 4px;
  height: 9px;
  border: solid white;
  border-width: 0 1px 1px 0;
  -webkit-transform: rotate(45deg);
  -ms-transform: rotate(45deg);
  transform: rotate(45deg);
}

.close {
  cursor: pointer;
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
}
</style>